import {
  AccountRelatedPath,
  BranchRelatedPath,
  ExportScanParams,
  getAccountRelatedUrl,
  ListBranchesQueryParams,
  ListProjectsParams,
  ListPullRequestsParams,
  PaginationQueryParams,
  ProjectIgnoredFile,
  ProjectRelatedPath,
} from '..'

import { composePathAndParams, joinValues } from '@/utils/urls'

export const getBranchRelatedUrl = (
  workspaceId: string,
  projectId: string,
  branchName: string
): string =>
  joinValues(
    getProjectRelatedUrl(workspaceId, projectId),
    'branches',
    encodeURIComponent(
      branchName ? branchName?.replaceAll('/', ' ') : '' // https://app.asana.com/0/1200113379311181/1201104154685942/f
    )
  )

export const getProjectIssueRelatedUrl = (
  workspaceId: string,
  issueId: string,
  projectId: string
): string =>
  joinValues(getProjectRelatedUrl(workspaceId, projectId), 'issues', issueId)

export const getProjectRelatedUrl = (
  workspaceId: string,
  projectId: string
): string =>
  joinValues(getAccountRelatedUrl(workspaceId), 'projects', projectId)

export const projectUrls = {
  getUserProjects: ({ workspaceId, userId }): string => {
    return joinValues(
      `/rest/workspaces`,
      workspaceId,
      'users',
      userId,
      'projects?page=0&size=9&sort=project.dateLastActivity,desc'
    )
  },
  getProjectSchedule: ({ workspaceId, projectId }): string => {
    return joinValues(
      `/rest/workspaces`,
      workspaceId,
      'projects',
      projectId,
      'settings/schedules'
    )
  },
  installProjectPolicies: ({ workspaceId, projectId }): string => {
    return joinValues(
      `/rest/workspaces`,
      workspaceId,
      'projects',
      projectId,
      'waf-migration/migrate-policies'
    )
  },
  getProjectMigrationStatus: ({ workspaceId, projectId }): string => {
    return joinValues(
      `/rest/workspaces`,
      workspaceId,
      'projects',
      projectId,
      'waf-migration/policies-migrated'
    )
  },
  getScanIssuesByFile: ({
    workspaceId,
    scanId,
    projectId,
    params,
  }: ProjectRelatedPath & {
    scanId: string
    params?: PaginationQueryParams & {
      include_children?: boolean
      base_path?: string
      from_path?: string
      sort_alphabetically?: boolean
    }
  }) => {
    const path = joinValues(
      `/rest/workspaces`,
      workspaceId,
      'projects',
      projectId,
      'scans',
      scanId,
      'locations'
    )
    return composePathAndParams(path, params)
  },
  getCloseProjectUrl: ({
    workspaceId,
    projectId,
  }: ProjectRelatedPath): string => {
    return joinValues(getProjectRelatedUrl(workspaceId, projectId), 'close')
  },
  getIssueTrackings: ({
    workspaceId,
    projectId,
  }: ProjectRelatedPath): string => {
    return joinValues(
      getProjectRelatedUrl(workspaceId, projectId),
      'issue-trackings'
    )
  },
  getProjectInfo: ({ workspaceId, projectId }: ProjectRelatedPath): string => {
    return getProjectRelatedUrl(workspaceId, projectId)
  },
  getProjectNameLookup: ({ workspaceId }: { workspaceId: string }): string => {
    return joinValues(
      getAccountRelatedUrl(workspaceId),
      'projects',
      'verify-name'
    )
  },
  getProjectBenchmark: ({ workspaceId, projectId }: ProjectRelatedPath) => {
    return joinValues(
      getProjectRelatedUrl(workspaceId, projectId),
      'normalized_defect_density_benchmark'
    )
  },

  getProjectScanBenchmark: ({
    workspaceId,
    projectId,
    scanId,
  }: ProjectRelatedPath & { scanId: string }) => {
    return joinValues(
      getProjectRelatedUrl(workspaceId, projectId),
      'scans',
      scanId,
      'normalized_defect_density_benchmark'
    )
  },
  getProjectWebhook: ({
    workspaceId,
    projectId,
    hookId,
  }: ProjectRelatedPath & { hookId: string }) => {
    return joinValues(
      getProjectRelatedUrl(workspaceId, projectId),
      'outbound-hooks',
      hookId
    )
  },
  getProjectWebhooks: ({
    workspaceId,
    projectId,
    params,
  }: ProjectRelatedPath & { params?: PaginationQueryParams }) => {
    const path = joinValues(
      getProjectRelatedUrl(workspaceId, projectId),
      'outbound-hooks'
    )
    return composePathAndParams(path, params)
  },
  getProjectPolicies: ({
    workspaceId,
    projectId,

    params,
  }: ProjectRelatedPath & {
    params?: PaginationQueryParams
  }): string => {
    const path = joinValues(
      getProjectRelatedUrl(workspaceId, projectId),
      'policies2'
    )
    return composePathAndParams(path, params)
  },
  listProjectIgnoredFiles: ({
    workspaceId,
    projectId,
    params,
  }: any): string => {
    const path = joinValues(
      getProjectRelatedUrl(workspaceId, projectId),
      'ignored_files'
    )
    return composePathAndParams(path, params)
  },
  getProjectDeleteIgnoredFile: ({
    workspaceId,
    projectId,
    ignoredFile,
  }: ProjectIgnoredFile): string => {
    return joinValues(
      getProjectRelatedUrl(workspaceId, projectId),
      'ignored_files',
      ignoredFile
    )
  },
  getProjectBulkIgnoredFiles: ({
    workspaceId,
    projectId,
  }: ProjectRelatedPath): string => {
    return joinValues(
      getProjectRelatedUrl(workspaceId, projectId),
      'bulk_ignored_files'
    )
  },
  getProjectSettings: ({
    workspaceId,
    projectId,
  }: ProjectRelatedPath): string => {
    return joinValues(getProjectRelatedUrl(workspaceId, projectId), 'settings')
  },
  getProjectConnection: ({
    workspaceId,
    projectId,
  }: ProjectRelatedPath): string => {
    return joinValues(
      getProjectRelatedUrl(workspaceId, projectId),
      'connection'
    )
  },
  getSlackChannels: ({
    connectionId,
    workspaceId,
    projectId,
  }: ProjectRelatedPath & {
    connectionId: string
    params?: PaginationQueryParams
  }): string => {
    return joinValues(
      getProjectRelatedUrl(workspaceId, projectId),
      'slack',
      connectionId,
      'channels'
    )
  },
  getSlackSettings: ({
    workspaceId,
    projectId,
  }: ProjectRelatedPath): string => {
    return joinValues(
      getProjectRelatedUrl(workspaceId, projectId),
      'settings',
      'slack'
    )
  },
  getProjectTeams: ({
    workspaceId,
    userId,
  }: AccountRelatedPath & { userId: string }): string => {
    return joinValues(getAccountRelatedUrl(workspaceId), 'users', userId)
  },
  getProjectTeamsUser: ({
    workspaceId,
    projectId,
    userId,
  }: ProjectRelatedPath & { userId: string }): string => {
    return joinValues(
      getProjectRelatedUrl(workspaceId, projectId),
      'users',
      userId
    )
  },
  getProjectTeamsUsers: ({
    workspaceId,
    projectId,
    params,
  }: ProjectRelatedPath & { params: PaginationQueryParams }): string => {
    const path = joinValues(
      getProjectRelatedUrl(workspaceId, projectId),
      `users`
    )
    return composePathAndParams(path, params)
  },
  getReopenProjectUrl: ({
    workspaceId,
    projectId,
  }: ProjectRelatedPath): string => {
    return joinValues(getProjectRelatedUrl(workspaceId, projectId), `reopen`)
  },
  listActiveEnvironments: ({
    workspaceId,
    params,
  }: ListProjectsParams): string => {
    const allParameters = Object.assign(
      {
        statuses: ['ACTIVE', 'BASELINE', 'CONNECTION_PENDING'],
        types: 'ENVIRONMENT',
      },
      params
    )
    const path = joinValues(getAccountRelatedUrl(workspaceId), 'projects')
    return composePathAndParams(path, allParameters)
  },
  listActiveGitProjects: ({
    workspaceId,
    params,
  }: ListProjectsParams): string => {
    const allParameters = Object.assign(
      {
        statuses: ['ACTIVE', 'BASELINE', 'CONNECTION_PENDING'],
        types: 'GIT',
      },
      params
    )
    const path = joinValues(getAccountRelatedUrl(workspaceId), 'projects')
    return composePathAndParams(path, allParameters)
  },
  listActiveProjects: ({
    workspaceId,
    params,
  }: ListProjectsParams & {
    params: { statuses?: string[]; types?: string[] }
  }): string => {
    const allParameters = Object.assign(
      {
        statuses: ['ACTIVE', 'BASELINE', 'CONNECTION_PENDING'],
        types: ['ENVIRONMENT', 'GIT'],
      },
      params
    )
    const path = joinValues(getAccountRelatedUrl(workspaceId), 'projects')
    return composePathAndParams(path, allParameters)
  },
  listProjectUsers: ({
    workspaceId,
    projectId,
    params,
  }: ProjectRelatedPath & { params: PaginationQueryParams }): string => {
    const path = joinValues(
      getProjectRelatedUrl(workspaceId, projectId),
      'users'
    )
    return composePathAndParams(path, params)
  },
  listBranches: ({
    workspaceId,
    projectId,
    params,
  }: ProjectRelatedPath & { params: ListBranchesQueryParams }): string => {
    const path = joinValues(
      getProjectRelatedUrl(workspaceId, projectId),
      'branches'
    )
    return composePathAndParams(path, params)
  },
  listRemoteBranches: ({
    workspaceId,
    projectId,
    params,
  }: ProjectRelatedPath & { params: PaginationQueryParams }): string => {
    const path = joinValues(
      getProjectRelatedUrl(workspaceId, projectId),
      'remote_branches'
    )
    return composePathAndParams(path, params)
  },
  listRemotePullRequests: ({
    workspaceId,
    projectId,
    params,
  }: ProjectRelatedPath & { params: PaginationQueryParams }): string => {
    const path = joinValues(
      getProjectRelatedUrl(workspaceId, projectId),
      'remote_pulls'
    )
    return composePathAndParams(path, params)
  },
  branches: {
    getBranchUrl: ({
      branchName,
      workspaceId,
      projectId,
    }: BranchRelatedPath): string => {
      return getBranchRelatedUrl(workspaceId, projectId, branchName)
    },
    getTrackUrl: ({
      workspaceId,
      projectId,
      branchName,
    }: BranchRelatedPath): string => {
      return joinValues(
        getBranchRelatedUrl(workspaceId, projectId, branchName),
        'track'
      )
    },
  },
  diffchanges: {
    getDiffChangesScanReportUrl: ({
      workspaceId,
      diffId,
      projectId,
    }: ProjectRelatedPath & { diffId: string }): string =>
      joinValues(
        getProjectRelatedUrl(workspaceId, projectId),
        'DIFFS'.toLowerCase(),
        diffId
      ),
    listByBranchName: ({
      workspaceId,
      projectId,
      params,
    }: ListPullRequestsParams): string => {
      const path = joinValues(
        getProjectRelatedUrl(workspaceId, projectId),
        'DIFFS'.toLowerCase()
      )
      return composePathAndParams(path, params)
    },
  },
  pullrequests: {
    getPullRequestUrl: ({
      workspaceId,
      pullRequestId,
      projectId,
    }: ProjectRelatedPath & { pullRequestId: string }): string =>
      joinValues(
        getProjectRelatedUrl(workspaceId, projectId),
        'pull-requests',
        pullRequestId
      ),
    listByBranchName: ({
      workspaceId,
      projectId,
      params,
    }: ListPullRequestsParams): string => {
      const path = joinValues(
        getProjectRelatedUrl(workspaceId, projectId),
        'pull-requests'
      )
      return composePathAndParams(path, params)
    },
    getPullRequestIssuesByLocation: ({
      projectId,
      pullRequestId,
      workspaceId,
      params,
    }: ProjectRelatedPath & {
      pullRequestId: string
      params?: PaginationQueryParams & {
        include_children?: boolean
        base_path?: string
        from_path?: string
        sort_alphabetically?: boolean
      }
    }) => {
      const path = joinValues(
        getProjectRelatedUrl(workspaceId, projectId),
        'pull-request-reports',
        pullRequestId,
        'locations'
      )
      return composePathAndParams(path, params)
    },
  },

  revisions: {
    listRevisionsByBranchName: ({
      workspaceId,
      projectId,
      branchName,
      params,
    }: BranchRelatedPath & { params: PaginationQueryParams }): string => {
      const path = joinValues(
        getBranchRelatedUrl(workspaceId, projectId, branchName),
        'revisions'
      )
      return composePathAndParams(path, params)
    },
  },
  scans: {
    getExportScanAllProblems: ({
      workspaceId,
      projectId,
      scanId,
      params,
    }: ProjectRelatedPath & {
      scanId: string
      params: ExportScanParams
    }): string => {
      const path = joinValues(
        projectUrls.scans.getScanByIdUrl({
          workspaceId,
          projectId,
          scanId,
        }),
        `export`
      )
      return composePathAndParams(path, params)
    },
    getExportScanProblemsByPolicyId: (
      {
        workspaceId,
        policyId,
        projectId,
        scanId,
        params,
      }: ProjectRelatedPath & {
        policyId: string
        scanId: string
        params: ExportScanParams
      },
      scanType = 'REVISION'
    ): string => {
      const path = joinValues(
        projectUrls.scans.getScanByIdUrl(
          { workspaceId, projectId, scanId },
          scanType
        ),
        'policies',
        policyId,
        'export'
      )
      return composePathAndParams(path, params)
    },
    getExportScanProblemsByRuleId: (
      {
        workspaceId,
        policyId,
        projectId,
        ruleId,
        scanId,
        params,
      }: ProjectRelatedPath & {
        policyId: string
        ruleId: string
        scanId: string
        params: ExportScanParams
      },
      scanType = 'REVISION'
    ): string => {
      const path = joinValues(
        projectUrls.scans.getScanByIdUrl(
          { workspaceId, projectId, scanId },
          scanType
        ),
        'policies',
        policyId,
        'rules',
        ruleId,
        'export'
      )
      return composePathAndParams(path, params)
    },
    getExportScanProblemsByScanId: (
      {
        workspaceId,
        projectId,
        scanId,
        params,
      }: ProjectRelatedPath & {
        scanId: string
        params: ExportScanParams
      },
      scanType = 'REVISION'
    ): string => {
      const path = joinValues(
        projectUrls.scans.getScanByIdUrl(
          {
            workspaceId,
            projectId,
            scanId,
          },
          scanType
        ),
        'export'
      )
      return composePathAndParams(path, params)
    },
    getLatestScanByBranch: ({
      workspaceId,
      projectId,
      branchName,
    }: BranchRelatedPath): string => {
      return joinValues(
        getBranchRelatedUrl(workspaceId, projectId, branchName),
        'latest_scan'
      )
    },
    getScanByIdUrl: (
      {
        workspaceId,
        params,
        projectId,
        scanId,
      }: ProjectRelatedPath & {
        scanId: string
        params?: { legacy_code: boolean; location?: string }
      },
      scanType = 'REVISION'
    ): string => {
      let subPath = 'scans'
      switch (scanType) {
        case 'DIFFS':
          subPath = 'diffs'
          break
        case 'PULL_REQUEST':
          subPath = 'pull-requests'
          break
        default:
          subPath = 'scans'
          break
      }
      const path = joinValues(
        getProjectRelatedUrl(workspaceId, projectId),
        subPath,
        scanId
      )
      return composePathAndParams(path, params)
    },
    getScanProblems: ({
      workspaceId,
      projectId,
      scanId,
      params,
    }: ProjectRelatedPath & { scanId: string } & {
      params?: PaginationQueryParams
    }): string => {
      const path = joinValues(
        projectUrls.scans.getScanByIdUrl({
          workspaceId,
          projectId,
          scanId,
        }),
        `problems`
      )
      return composePathAndParams(path, params)
    },
    getScanPolicyByIdUrl: ({
      workspaceId,
      params,
      projectId,
      scanId,
      scanPolicyId,
    }: ProjectRelatedPath & {
      scanId: string
      scanPolicyId: string
      params?: { legacy_code: boolean }
    }): string => {
      const path = joinValues(
        projectUrls.scans.getScanByIdUrl({ workspaceId, projectId, scanId }),
        'policies',
        scanPolicyId
      )
      return composePathAndParams(path, params)
    },
    getScanPolicyProblems: ({
      workspaceId,
      projectId,
      scanId,
      scanPolicyId,
      params,
    }: ProjectRelatedPath & {
      scanId: string
      scanPolicyId: string
    } & { params: PaginationQueryParams }): string => {
      const path = joinValues(
        projectUrls.scans.getScanPolicyByIdUrl({
          workspaceId,
          projectId,
          scanId,
          scanPolicyId,
        }),
        `problems`
      )
      return composePathAndParams(path, params)
    },
    getDiffsChangesScanRules: ({
      workspaceId,
      projectId,
      scanId,
      params,
    }: ProjectRelatedPath & {
      scanId: string
    } & { params: PaginationQueryParams }): string => {
      const path = joinValues(
        projectUrls.diffchanges.getDiffChangesScanReportUrl({
          workspaceId,
          projectId,
          diffId: scanId,
        }),
        `rules`
      )
      return composePathAndParams(path, params)
    },
    getPullRequestScanRules: ({
      workspaceId,
      projectId,
      scanId,
      params,
    }: ProjectRelatedPath & {
      scanId: string
    } & { params: PaginationQueryParams }): string => {
      const path = joinValues(
        projectUrls.pullrequests.getPullRequestUrl({
          workspaceId,
          projectId,
          pullRequestId: scanId,
        }),
        `rules`
      )
      return composePathAndParams(path, params)
    },
    getScanRules: ({
      workspaceId,
      projectId,
      scanId,
      params,
    }: ProjectRelatedPath & {
      scanId: string
    } & { params: PaginationQueryParams }): string => {
      const path = joinValues(
        projectUrls.scans.getScanByIdUrl({
          workspaceId,
          projectId,
          scanId,
        }),
        `rules`
      )
      return composePathAndParams(path, params)
    },
    getDiffChangesScanRuleByIdUrl: ({
      workspaceId,
      projectId,
      ruleId,
      scanId,
    }: ProjectRelatedPath & {
      ruleId: string
      scanId: string
    }): string => {
      return joinValues(
        projectUrls.diffchanges.getDiffChangesScanReportUrl({
          workspaceId,
          diffId: scanId,
          projectId,
        }),
        'rules',
        ruleId
      )
    },
    getPullRequestScanRuleByIdUrl: ({
      workspaceId,
      projectId,
      ruleId,
      scanId,
    }: ProjectRelatedPath & {
      ruleId: string
      scanId: string
    }): string => {
      return joinValues(
        projectUrls.pullrequests.getPullRequestUrl({
          workspaceId,
          projectId,
          pullRequestId: scanId,
        }),
        'rules',
        ruleId
      )
    },
    getScanRuleByIdUrl: (
      {
        workspaceId,
        projectId,
        ruleId,
        scanId,
        legacyCode,
      }: ProjectRelatedPath & {
        ruleId: string
        scanId: string
        legacyCode?: boolean
      },
      scanType = 'REVISION'
    ): string => {
      const params = {
        workspaceId,
        projectId,
        ruleId,
        scanId,
      }
      switch (scanType) {
        case 'DIFFS':
          return projectUrls.scans.getDiffChangesScanRuleByIdUrl(params)
        case 'PULL_REQUEST':
          return projectUrls.scans.getPullRequestScanRuleByIdUrl(params)
        default: {
          const path = joinValues(
            projectUrls.scans.getScanByIdUrl(params),
            'rules',
            ruleId
          )
          return composePathAndParams(path, { legacy_code: legacyCode })
        }
      }
    },
    getDiffChangesScanPolicyRuleByIdUrl: ({
      workspaceId,
      projectId,
      ruleId,
      scanId,
      scanPolicyId,
    }: ProjectRelatedPath & {
      ruleId: string
      scanId: string
      scanPolicyId: string
    }): string => {
      return joinValues(
        projectUrls.diffchanges.getDiffChangesScanReportUrl({
          workspaceId,
          diffId: scanId,
          projectId,
        }),
        'policies',
        scanPolicyId,
        'rules',
        ruleId
      )
    },
    getPullRequestScanPolicyRuleByIdUrl: ({
      workspaceId,
      projectId,
      ruleId,
      scanId,
      scanPolicyId,
      params,
    }: ProjectRelatedPath & {
      ruleId: string
      scanId: string
      scanPolicyId: string
      params?: { legacy_code: boolean }
    }): string => {
      const path = joinValues(
        projectUrls.pullrequests.getPullRequestUrl({
          workspaceId,
          projectId,
          pullRequestId: scanId,
        }),
        'policies',
        scanPolicyId,
        'rules',
        ruleId
      )

      return composePathAndParams(path, params)
    },
    getScanPolicyRuleByIdUrl: (
      {
        workspaceId,
        projectId,
        ruleId,
        scanId,
        scanPolicyId,
        legacyCode,
      }: ProjectRelatedPath & {
        ruleId: string
        scanId: string
        scanPolicyId: string
        legacyCode?: boolean
      },
      scanType = 'REVISION'
    ): string => {
      const params = {
        workspaceId,
        projectId,
        ruleId,
        scanId,
        scanPolicyId,
      }
      switch (scanType) {
        case 'DIFFS':
          return projectUrls.scans.getDiffChangesScanPolicyRuleByIdUrl(params)
        case 'PULL_REQUEST':
          return projectUrls.scans.getPullRequestScanPolicyRuleByIdUrl(params)
        default: {
          const path = joinValues(
            projectUrls.scans.getScanPolicyByIdUrl(params),
            'rules',
            ruleId
          )
          return composePathAndParams(path, {
            legacy_code: legacyCode,
          })
        }
      }
    },
    getDiffChangesScanPolicyRules: ({
      workspaceId,
      projectId,
      scanId,
      scanPolicyId,
      params,
    }: ProjectRelatedPath & {
      scanId: string
      scanPolicyId: string
    } & { params: PaginationQueryParams }): string => {
      const path = joinValues(
        projectUrls.diffchanges.getDiffChangesScanReportUrl({
          workspaceId,
          projectId,
          diffId: scanId,
        }),
        'policies',
        scanPolicyId,
        'rules'
      )
      return composePathAndParams(path, params)
    },
    getPullRequestScanPolicyRules: ({
      workspaceId,
      projectId,
      scanId,
      scanPolicyId,
      params,
    }: ProjectRelatedPath & {
      scanId: string
      scanPolicyId: string
    } & { params: PaginationQueryParams }): string => {
      const path = joinValues(
        projectUrls.pullrequests.getPullRequestUrl({
          workspaceId,
          projectId,
          pullRequestId: scanId,
        }),
        'policies',
        scanPolicyId,
        'rules'
      )
      return composePathAndParams(path, params)
    },
    getScanPolicyRules: ({
      workspaceId,
      projectId,
      scanId,
      scanPolicyId,
      params,
    }: ProjectRelatedPath & {
      scanId: string
      scanPolicyId: string
    } & { params: PaginationQueryParams }): string => {
      const path = joinValues(
        projectUrls.scans.getScanPolicyByIdUrl({
          workspaceId,
          projectId,
          scanId,
          scanPolicyId,
        }),
        `rules`
      )

      return composePathAndParams(path, params)
    },
    getDiffChangesScanPolicyRuleProblems: ({
      workspaceId,
      projectId,
      ruleId,
      scanId,
      scanPolicyId,
      params,
    }: ProjectRelatedPath & {
      ruleId: string
      scanId: string
      scanPolicyId: string
    } & { params: PaginationQueryParams }): string => {
      const path = joinValues(
        projectUrls.diffchanges.getDiffChangesScanReportUrl({
          workspaceId,
          diffId: scanId,
          projectId,
        }),
        'policies',
        scanPolicyId,
        'rules',
        ruleId,
        'problems'
      )
      return composePathAndParams(path, params)
    },
    getPullRequestScanPolicyRuleProblems: ({
      workspaceId,
      projectId,
      ruleId,
      scanId,
      scanPolicyId,
      params,
    }: ProjectRelatedPath & {
      ruleId: string
      scanId: string
      scanPolicyId: string
    } & { params: PaginationQueryParams }): string => {
      const path = joinValues(
        projectUrls.pullrequests.getPullRequestUrl({
          workspaceId,
          projectId,
          pullRequestId: scanId,
        }),
        'policies',
        scanPolicyId,
        'rules',
        ruleId,
        'problems'
      )
      return composePathAndParams(path, params)
    },
    getScanPolicyRuleProblems: ({
      workspaceId,
      projectId,
      ruleId,
      scanId,
      scanPolicyId,
      params,
    }: ProjectRelatedPath & {
      ruleId: string
      scanId: string
      scanPolicyId: string
    } & { params: PaginationQueryParams }): string => {
      const path = joinValues(
        projectUrls.scans.getScanPolicyByIdUrl({
          workspaceId,
          projectId,
          scanId,
          scanPolicyId,
        }),
        'rules',
        ruleId,
        'problems'
      )
      return composePathAndParams(path, params)
    },
    getDiffChangesScanRuleProblems: ({
      workspaceId,
      projectId,
      ruleId,
      scanId,
      params,
    }: ProjectRelatedPath & {
      ruleId: string
      scanId: string
    } & { params: PaginationQueryParams }): string => {
      const path = joinValues(
        projectUrls.diffchanges.getDiffChangesScanReportUrl({
          workspaceId,
          diffId: scanId,
          projectId,
        }),
        'rules',
        ruleId,
        'problems'
      )
      return composePathAndParams(path, params)
    },
    getPullRequestScanRuleProblems: ({
      workspaceId,
      projectId,
      ruleId,
      scanId,
      params,
    }: ProjectRelatedPath & {
      ruleId: string
      scanId: string
    } & { params: PaginationQueryParams }): string => {
      const path = joinValues(
        projectUrls.pullrequests.getPullRequestUrl({
          workspaceId,
          projectId,
          pullRequestId: scanId,
        }),
        'rules',
        ruleId,
        'problems'
      )
      return composePathAndParams(path, params)
    },
    getScanRuleProblems: ({
      workspaceId,
      projectId,
      ruleId,
      scanId,
      params,
    }: ProjectRelatedPath & {
      ruleId: string
      scanId: string
    } & { params: PaginationQueryParams }): string => {
      const path = joinValues(
        projectUrls.scans.getScanByIdUrl({
          workspaceId,
          projectId,
          scanId,
        }),
        'rules',
        ruleId,
        'problems'
      )
      return composePathAndParams(path, params)
    },
    getScans: ({
      workspaceId,
      projectId,
      params,
    }: ProjectRelatedPath & {
      params: PaginationQueryParams & { statuses?: string[] }
    }): string => {
      const path = joinValues(
        getProjectRelatedUrl(workspaceId, projectId),
        'scans'
      )
      return composePathAndParams(path, params)
    },
    getScansByBranchUrl: ({
      workspaceId,
      projectId,
    }: ProjectRelatedPath): string =>
      joinValues(
        getProjectRelatedUrl(workspaceId, projectId),
        'scans/by_branch'
      ),
    getScansByBranchAndRevisionUrl: ({
      workspaceId,
      projectId,
    }: ProjectRelatedPath): string =>
      joinValues(
        getProjectRelatedUrl(workspaceId, projectId),
        'scans/by_branch_and_revision'
      ),
    getScansByRevisionUrl: ({
      workspaceId,
      projectId,
    }: ProjectRelatedPath): string =>
      joinValues(
        getProjectRelatedUrl(workspaceId, projectId),

        'scans/by_revision'
      ),
    getScansByPullRequestUrl: (
      workspaceId: string,
      projectId: string
    ): string =>
      joinValues(
        getProjectRelatedUrl(workspaceId, projectId),
        'scans/by_pull_request'
      ),
  },
  snapshots: {
    getExportProjectSnapshotUrl: (
      workspaceId: string,
      projectId: string,
      snapshotId: string
    ): string =>
      joinValues(
        getProjectRelatedUrl(workspaceId, projectId),
        'cx/snapshots',
        snapshotId,
        'export'
      ),
  },
  tasks: {
    getTaskUrl: ({
      workspaceId,
      projectId,
      taskId,
    }: ProjectRelatedPath & { taskId: string }): string =>
      joinValues(
        getAccountRelatedUrl(workspaceId),
        'projects',
        projectId,
        'tasks',
        taskId
      ),
  },
}
